/**
 * Logs Service
 * Handles log entry retrieval and management operations
 * Requires admin authorization
 */

import api from './apiClient';

/**
 * @typedef {Object} LogEntry
 * @property {number} id - Log entry ID
 * @property {string} timestamp - Log timestamp
 * @property {string} level - Log level (Information, Warning, Error, Critical)
 * @property {string} message - Log message
 * @property {string} [exception] - Exception details if any
 * @property {string} [correlationId] - Request correlation ID
 * @property {string} [meta] - Additional metadata as JSON string
 * @property {string} dateCreated - Creation timestamp
 * @property {string} dateModified - Last modified timestamp
 */

/**
 * @typedef {Object} LogPagedResult
 * @property {LogEntry[]} items - Array of log entries
 * @property {number} totalCount - Total number of entries
 * @property {number} page - Current page number
 * @property {number} pageSize - Items per page
 */

/**
 * @typedef {Object} LogStatistics
 * @property {number} totalCount - Total log entries
 * @property {number} errorCount - Error level count
 * @property {number} warningCount - Warning level count
 * @property {number} informationCount - Information level count
 * @property {number} criticalCount - Critical level count
 * @property {string} [oldestLog] - Oldest log timestamp
 * @property {string} [newestLog] - Newest log timestamp
 */

/**
 * @typedef {Object} LogQueryParams
 * @property {number} [page=1] - Page number
 * @property {number} [pageSize=20] - Items per page
 * @property {string} [level] - Filter by log level
 * @property {string} [from] - Filter from date (ISO string)
 * @property {string} [to] - Filter to date (ISO string)
 * @property {string} [correlationId] - Filter by correlation ID
 */

/**
 * Log levels for filtering
 */
export const LogLevels = {
  Information: 'Information',
  Warning: 'Warning',
  Error: 'Error',
  Critical: 'Critical',
};

/**
 * Log level colors for UI
 */
export const LogLevelColors = {
  Information: '#1890ff', // Blue
  Warning: '#faad14',     // Orange
  Error: '#ff4d4f',       // Red
  Critical: '#722ed1',    // Purple
};

/**
 * Get paginated list of log entries
 * @param {LogQueryParams} [params={}] - Query parameters
 * @param {Object} [options] - Request options
 * @returns {Promise<LogPagedResult>}
 */
export const getLogs = async (params = {}, options = {}) => {
  return api.get('/logs', params, options);
};

/**
 * Get a single log entry by ID
 * @param {number} id - Log entry ID
 * @param {Object} [options] - Request options
 * @returns {Promise<LogEntry>}
 */
export const getLog = async (id, options = {}) => {
  return api.get(`/logs/${id}`, {}, options);
};

/**
 * Get log statistics
 * @param {Object} [options] - Request options
 * @returns {Promise<LogStatistics>}
 */
export const getStatistics = async (options = {}) => {
  return api.get('/logs/statistics', {}, options);
};

/**
 * @typedef {Object} TimelineBucket
 * @property {string} timestamp - Bucket start timestamp
 * @property {number} errorCount - Error count in bucket
 * @property {number} warningCount - Warning count in bucket
 * @property {number} criticalCount - Critical count in bucket
 * @property {number} informationCount - Information count in bucket
 * @property {number} totalCount - Total count in bucket
 */

/**
 * @typedef {Object} TimelineResponse
 * @property {string} period - The period (day, week, month, 3months)
 * @property {string} startDate - Start date of the timeline
 * @property {string} endDate - End date of the timeline
 * @property {TimelineBucket[]} buckets - Array of time buckets
 */

/**
 * Timeline periods
 */
export const TimelinePeriods = {
  Day: 'day',
  Week: 'week',
  Month: 'month',
  ThreeMonths: '3months',
};

/**
 * Get log timeline statistics for charting
 * @param {string} [period='week'] - Period: day, week, month, 3months
 * @param {Object} [options] - Request options
 * @returns {Promise<TimelineResponse>}
 */
export const getTimeline = async (period = 'week', options = {}) => {
  return api.get('/logs/statistics/timeline', { period }, options);
};

/**
 * Delete a single log entry
 * @param {number} id - Log entry ID
 * @param {Object} [options] - Request options
 * @returns {Promise<{deletedCount: number, message: string}>}
 */
export const deleteLog = async (id, options = {}) => {
  return api.delete(`/logs/${id}`, {
    ...options,
    successMessage: 'Log entry deleted',
  });
};

/**
 * Delete multiple log entries based on criteria
 * @param {Object} criteria - Deletion criteria
 * @param {string} [criteria.olderThan] - Delete entries older than this date (ISO string)
 * @param {string} [criteria.level] - Delete entries with this log level
 * @param {Object} [options] - Request options
 * @returns {Promise<{deletedCount: number, message: string}>}
 */
export const deleteLogs = async (criteria = {}, options = {}) => {
  return api.delete('/logs', {
    ...options,
    data: criteria,
  });
};

/**
 * Delete all log entries (use with caution)
 * @param {Object} [options] - Request options
 * @returns {Promise<{deletedCount: number, message: string}>}
 */
export const deleteAllLogs = async (options = {}) => {
  return api.delete('/logs/all', {
    ...options,
    successMessage: 'All log entries deleted',
  });
};

/**
 * Test exception logging (development only)
 * Triggers a test exception to verify logging is working
 * @param {Object} [options] - Request options
 * @returns {Promise<never>} Always throws an error
 */
export const testException = async (options = {}) => {
  return api.post('/logs/test-exception', {}, {
    ...options,
    suppressNotification: true, // We expect this to fail
  });
};

/**
 * Parse log metadata from JSON string
 * @param {string} metaJson - Metadata JSON string
 * @returns {Object|null} Parsed metadata or null
 */
export const parseMetadata = (metaJson) => {
  if (!metaJson) return null;
  try {
    return JSON.parse(metaJson);
  } catch {
    return null;
  }
};

/**
 * Format log timestamp for display
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Formatted timestamp
 */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp);
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

/**
 * Get relative time string (e.g., "5 minutes ago")
 * @param {string} timestamp - ISO timestamp
 * @returns {string} Relative time string
 */
export const getRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);
  const diffDay = Math.floor(diffHour / 24);

  if (diffSec < 60) return 'just now';
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`;
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`;
  if (diffDay < 7) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`;
  
  return formatTimestamp(timestamp);
};

const logsService = {
  getLogs,
  getLog,
  getStatistics,
  getTimeline,
  deleteLog,
  deleteLogs,
  deleteAllLogs,
  testException,
  parseMetadata,
  formatTimestamp,
  getRelativeTime,
  LogLevels,
  LogLevelColors,
  TimelinePeriods,
};

export default logsService;
